import { Badge, Box, Button, Menu, Portal, Table, Text } from '@chakra-ui/react'
import React from 'react'
import { Link } from 'react-router'

import { Checkbox } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'

import { useExtensionsI18n } from '../i18n/provider'
import type {
  ExtensionForDisplay,
  LineForDisplay,
} from '../types/extension.types'
import { isInactiveExtension } from '../types/extension.types'
import { getCallMetricsUrl, getExtensionTypeLabel } from '../utils/extensions'
import { MoreMenuIcon } from './TableIcons'

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}

const formatDate = (
  dateString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = '\u2014',
): string => {
  if (!dateString) return fallback
  try {
    return new Date(dateString).toLocaleString(undefined, options)
  } catch {
    return fallback
  }
}

const SUPPORTED_EDIT_TYPES = new Set<ExtensionType>([
  ExtensionType.LINE,
  ExtensionType.RING_GROUP,
  ExtensionType.CALL_QUEUE,
  ExtensionType.CONFERENCE_ROOM,
  ExtensionType.VIRTUAL_FAX,
  ExtensionType.SHARED,
  ExtensionType.DIAL_PLAN,
])

const hasSupportedEditRoute = (extension: ExtensionForDisplay): boolean =>
  SUPPORTED_EDIT_TYPES.has(extension.type as ExtensionType)

const getLocalizedExtensionTypeLabel = (
  type: ExtensionType,
  text: (key: string, fallback: string) => string,
): string => {
  switch (type) {
    case ExtensionType.LINE:
      return text('extensions.types.line', 'Line')
    case ExtensionType.RING_GROUP:
      return text('extensions.types.ringGroup', 'Ring Group')
    case ExtensionType.VIRTUAL_FAX:
      return text('extensions.types.virtualFax', 'Virtual Fax Machine')
    case ExtensionType.CONFERENCE_ROOM:
      return text('extensions.types.conferenceRoom', 'Conference Room')
    case ExtensionType.DIAL_PLAN:
      return text('extensions.types.switchboard', 'Switchboard')
    case ExtensionType.SHARED:
      return text('extensions.types.sharedVoicemail', 'Shared Voicemail Box')
    case ExtensionType.CALL_QUEUE:
      return text('extensions.types.callQueue', 'Call Queue')
    default:
      return getExtensionTypeLabel(type)
  }
}

export interface ExtensionRowProps {
  extension: ExtensionForDisplay
  pbxId: string
  canEdit: boolean
  canDelete: boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
}

export const ExtensionRow: React.FC<ExtensionRowProps> = ({
  extension,
  pbxId,
  canEdit,
  canDelete,
  onDeleteClick,
  getEditRoute,
}) => {
  const { text } = useExtensionsI18n()
  const isInactive = isInactiveExtension(extension)
  const displayName =
    extension.name ||
    extension.number ||
    text('extensions.row.extensionFallback', 'Extension {{id}}', {
      id: extension.id,
    })
  const canOpenEdit = canEdit && hasSupportedEditRoute(extension)

  const editPath = getEditRoute(pbxId, extension)

  return (
    <Table.Row>
      <Table.Cell px={4} py={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {canOpenEdit ? (
            <Link
              to={editPath}
              style={{
                color: 'var(--chakra-colors-blue-500)',
                textDecoration: 'none',
                fontWeight: 'var(--chakra-fontWeights-medium)',
              }}
            >
              {extension.number ?? '\u2014'}
            </Link>
          ) : (
            <Text fontWeight="medium">{extension.number ?? '\u2014'}</Text>
          )}
          {isInactive && (
            <Badge colorPalette="neutral" size="sm">
              {text('extensions.row.inactive', 'Inactive')}
            </Badge>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm">{displayName}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {getLocalizedExtensionTypeLabel(
            extension.type as ExtensionType,
            text,
          )}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(extension.updated_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(extension.created_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        {(canOpenEdit || canDelete) && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button size="sm" variant="ghost" colorPalette="neutral" px={2}>
                <MoreMenuIcon />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content minW="160px">
                  {canOpenEdit && (
                    <Menu.Item value="edit" asChild>
                      <Link to={editPath}>
                        {text('extensions.row.edit', 'Edit')}
                      </Link>
                    </Menu.Item>
                  )}
                  {canDelete && (
                    <Menu.Item
                      value="delete"
                      color="fg.error"
                      onClick={() => onDeleteClick(extension)}
                    >
                      {text('extensions.row.delete', 'Delete')}
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

export interface LineRowProps {
  line: LineForDisplay
  pbxId: string
  canEdit: boolean
  canDelete: boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  onToggleActiveClick?: (line: LineForDisplay) => void
  isSelected?: boolean
  onToggleSelect?: (lineId: number) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
  grafanaBaseUrl?: string
}

export const LineRow: React.FC<LineRowProps> = ({
  line,
  pbxId,
  canEdit,
  canDelete,
  onDeleteClick,
  onToggleActiveClick,
  isSelected,
  onToggleSelect,
  getEditRoute,
  grafanaBaseUrl,
}) => {
  const { text } = useExtensionsI18n()
  const isInactive = isInactiveExtension(line)
  const displayName =
    line.name ||
    line.number ||
    text('extensions.row.extensionFallback', 'Extension {{id}}', {
      id: line.id,
    })
  const callMetricsHref = getCallMetricsUrl(grafanaBaseUrl, line.uuid)
  const showCallMetricsLink = Boolean(grafanaBaseUrl && line.uuid)
  const canOpenEdit = canEdit && hasSupportedEditRoute(line)

  const editPath = getEditRoute(pbxId, line)

  return (
    <Table.Row>
      {onToggleSelect != null && (
        <Table.Cell px={4} py={3} w="40px">
          <Checkbox
            checked={isSelected === true}
            onCheckedChange={() => onToggleSelect(line.id)}
            inputProps={{
              'aria-label': text(
                'extensions.row.selectLine',
                'Select {{name}}',
                {
                  name: displayName,
                },
              ),
            }}
          />
        </Table.Cell>
      )}
      <Table.Cell px={4} py={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {canOpenEdit ? (
            <Link
              to={editPath}
              style={{
                color: 'var(--chakra-colors-blue-500)',
                textDecoration: 'none',
                fontWeight: 'var(--chakra-fontWeights-medium)',
              }}
            >
              {line.number ?? '\u2014'}
            </Link>
          ) : (
            <Text fontWeight="medium">{line.number ?? '\u2014'}</Text>
          )}
          {isInactive && (
            <Badge colorPalette="neutral" size="sm">
              {text('extensions.row.inactive', 'Inactive')}
            </Badge>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm">{displayName}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {line.assigned_profile?.name ?? line.assigned_user?.name ?? '\u2014'}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {line.location?.name ?? '\u2014'}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(line.created_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(line.updated_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button size="sm" variant="ghost" colorPalette="neutral" px={2}>
              <MoreMenuIcon />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="180px">
                {canOpenEdit && (
                  <Menu.Item value="edit" asChild>
                    <Link to={editPath}>
                      {text('extensions.row.edit', 'Edit')}
                    </Link>
                  </Menu.Item>
                )}
                {showCallMetricsLink && callMetricsHref && (
                  <Menu.Item value="call-metrics" asChild>
                    <a
                      href={callMetricsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {text(
                        'extensions.row.viewCallMetrics',
                        'View call metrics',
                      )}
                    </a>
                  </Menu.Item>
                )}
                {onToggleActiveClick && (
                  <Menu.Item
                    value="toggle-active"
                    onClick={() => onToggleActiveClick(line)}
                  >
                    {isInactive
                      ? text('extensions.row.activate', 'Activate')
                      : text('extensions.row.deactivate', 'Deactivate')}
                  </Menu.Item>
                )}
                {canDelete && (
                  <Menu.Item
                    value="delete"
                    color="fg.error"
                    onClick={() => onDeleteClick(line)}
                  >
                    {text('extensions.row.delete', 'Delete')}
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Table.Cell>
    </Table.Row>
  )
}

export const OtherExtensionRow: React.FC<
  ExtensionRowProps & { showActions?: boolean }
> = ({
  extension,
  pbxId,
  canEdit,
  canDelete,
  onDeleteClick,
  getEditRoute,
  showActions = true,
}) => {
  const { text } = useExtensionsI18n()
  const displayName =
    extension.name ||
    extension.number ||
    text('extensions.row.extensionFallback', 'Extension {{id}}', {
      id: extension.id,
    })
  const canOpenEdit = canEdit && hasSupportedEditRoute(extension)
  const editPath = getEditRoute(pbxId, extension)

  return (
    <Table.Row>
      <Table.Cell px={4} py={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {canOpenEdit ? (
            <Link
              to={editPath}
              style={{
                color: 'var(--chakra-colors-blue-500)',
                textDecoration: 'none',
                fontWeight: 'var(--chakra-fontWeights-medium)',
              }}
            >
              {extension.number ?? '\u2014'}
            </Link>
          ) : (
            <Text fontWeight="medium">{extension.number ?? '\u2014'}</Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm">{displayName}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(extension.created_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(extension.updated_at, dateTimeOptions)}
        </Text>
      </Table.Cell>
      {showActions && (
        <Table.Cell px={4} py={3}>
          {(canOpenEdit || canDelete) && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button size="sm" variant="ghost" colorPalette="neutral" px={2}>
                  <MoreMenuIcon />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content minW="160px">
                    {canOpenEdit && (
                      <Menu.Item value="edit" asChild>
                        <Link to={editPath}>
                          {text('extensions.row.edit', 'Edit')}
                        </Link>
                      </Menu.Item>
                    )}
                    {canDelete && (
                      <Menu.Item
                        value="delete"
                        color="fg.error"
                        onClick={() => onDeleteClick(extension)}
                      >
                        {text('extensions.row.delete', 'Delete')}
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          )}
        </Table.Cell>
      )}
    </Table.Row>
  )
}
